import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['discardButton', 'submitButton'];

  declare discardButtonTarget?: HTMLButtonElement;
  declare hasDiscardButtonTarget: boolean;
  declare submitButtonTarget: HTMLInputElement;
  declare dirty: boolean;
  declare boundPreventDirtyUnload: (event: Event) => any;

  connect() {
    this.dirty = false;
    this.boundPreventDirtyUnload = this.preventDirtyUnload.bind(this);
    window.addEventListener('beforeunload', this.boundPreventDirtyUnload);
    this.element.addEventListener('submit', this.handleSubmit.bind(this));
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.boundPreventDirtyUnload);
  }

  markDirty(event?: Event) {
    this.dirty = true;

    const input = event?.target as HTMLElement | undefined;
    if (input) {
      input.dataset.dirty = 'true';

      // If this input belongs to a logical section, mark the whole section dirty
      const group = input.getAttribute('data-group');
      if (group) {
        this.element
          .querySelectorAll<HTMLElement>(`[data-group="${group}"]`)
          .forEach((node) => (node.dataset.dirty = 'true'));
      }
    }

    // If this is a checkbox that is now UNCHECKED, mark Rails' hidden "0" field (same name) as dirty too.
    if (input instanceof HTMLInputElement && input.type === 'checkbox' && !input.checked) {
      const hidden = this.element.querySelector<HTMLInputElement>(
        `input[type="hidden"][name="${CSS?.escape ? CSS.escape(input.name) : input.name}"]`
      );
      if (hidden) hidden.dataset.dirty = 'true';
    }

    if (this.hasDiscardButtonTarget) this.discardButtonTarget?.removeAttribute('disabled');
    this.submitButtonTarget?.removeAttribute('disabled');
  }

  removeDirtyFlag() {
    this.dirty = false;
  }

  discardChanges(event: Event) {
    event.preventDefault();
    if (!this.dirty) return;

    // eslint-disable-next-line no-restricted-globals
    if (confirm('Are you sure you want to discard your changes without saving?')) {
      this.dirty = false;
      window.location.reload();
    }
  }

  preventDirtyUnload(event: BeforeUnloadEvent) {
    if (!this.dirty) return;
    event.preventDefault();
  }

  handleSubmit(_event: Event) {
    const inputs = this.element.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input, textarea, select'
    );

    // Build a set of groups that are dirty
    const dirtyGroups = new Set<string>();
    this.element
      .querySelectorAll<HTMLElement>('[data-dirty="true"][data-group]')
      .forEach((node) => dirtyGroups.add(node.getAttribute('data-group')!));

    inputs.forEach((input) => {
      const isMethodOverride = input.name === '_method';
      const isCsrfToken = input.name === 'authenticity_token';

      if (!input.dataset.dirty && !isMethodOverride && !isCsrfToken) {
        input.disabled = true;
        return;
      }

      const group = input.getAttribute('data-group');

      // If its group is dirty, send the entire section
      if (group && dirtyGroups.has(group)) {
        input.disabled = false;
        return;
      }

      // Otherwise only send individually dirty controls
      if (input.dataset.dirty === 'true') {
        input.disabled = false;
        
      }
    });
  }
}
